<template>
  <div class="page-wrapper">
    <!-- Background orbs -->
    <div class="bg-orb bg-orb-1"></div>
    <div class="bg-orb bg-orb-2"></div>

    <!-- ── Navbar ── -->
    <nav class="navbar">
      <router-link to="/" class="nav-brand">
        <img
          src="https://res.cloudinary.com/dldd8ucby/image/upload/v1587167979/RA/WhatsApp_Image_2020-04-16_at_10.51.15_AM.jpg"
          alt="G1BRA Logo"
          class="nav-logo"
        />
        <span class="nav-title">G1BRA</span>
      </router-link>

      <button class="nav-hamburger" @click="toggle">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="3" y1="6" x2="21" y2="6"/>
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>

      <div class="nav-links" :class="{ 'nav-links--open': open }">
        <router-link to="/login" class="nav-link">Login</router-link>
      </div>
    </nav>

    <!-- ── Main Content ── -->
    <main class="content">

      <!-- Page Header -->
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Examination Results</h1>
          <p class="page-subtitle">{{ filteredSubmissions.length }} submission{{ filteredSubmissions.length !== 1 ? 's' : '' }} recorded</p>
        </div>
        <vue-excel-xlsx
          :data="filteredSubmissions"
          :columns="columns"
          filename="Examination Results 2022"
          :sheetname="'sheetname'"
          class="export-btn"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Export Excel
        </vue-excel-xlsx>
      </div>

      <!-- Stats Row -->
      <div class="stats-row" v-if="submissions.length">
        <div class="stat-card">
          <span class="stat-label">Total Candidates</span>
          <span class="stat-value">{{ filteredSubmissions.length }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-label">Highest Score</span>
          <span class="stat-value">{{ highScore }}%</span>
        </div>
                <div class="stat-card">
          <span class="stat-label">Lowest Score</span>
          <span class="stat-value">{{ lowScore }}%</span>
        </div>
        <div class="stat-card">
          <span class="stat-label">Pass Rate (≥40%)</span>
          <span class="stat-value">{{ passRate }}%</span>
        </div>
      </div>

      <!-- Table Card -->
      <div class="table-card">
        <!-- Search -->
        <div class="table-toolbar">
          <div class="search-wrapper">
            <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              v-model="search"
              type="text"
              class="search-input"
              placeholder="Search by name, church, rank…"
            />
            <button v-if="search" class="search-clear" @click="search = ''">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="filter-wrapper">
            <select v-model="selectedChurch" class="filter-select">
              <option value="">All Churches</option>
              <option v-for="church in uniqueChurches" :key="church" :value="church">{{ titleCase(church) }}</option>
            </select>
            <select v-model="selectedRank" class="filter-select">
              <option value="">All Ranks</option>
              <option v-for="rank in uniqueRanks" :key="rank" :value="rank">{{ titleCase(rank) }}</option>
            </select>
            <button @click="clearFilters" class="clear-filters-btn">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              Clear
            </button>
          </div>
          <span class="results-count">{{ filteredSubmissions.length }} result{{ filteredSubmissions.length !== 1 ? 's' : '' }}</span>
        </div>

        <!-- Loading skeleton -->
        <div v-if="loading" class="skeleton-rows">
          <div v-for="n in 6" :key="n" class="skeleton-row">
            <div class="skeleton-cell skeleton-cell--wide"></div>
            <div class="skeleton-cell skeleton-cell--wide"></div>
            <div class="skeleton-cell skeleton-cell--med"></div>
            <div class="skeleton-cell skeleton-cell--narrow"></div>
            <div class="skeleton-cell skeleton-cell--narrow"></div>
          </div>
        </div>

        <!-- Table -->
        <div v-else class="table-wrap">
          <table class="results-table">
            <thead>
              <tr>
                <th @click="sortBy('index')" class="th-sortable">
                  #
                  <span class="sort-arrow" :class="sortArrowClass('index')">▲</span>
                </th>
                <th @click="sortBy('first_name')" class="th-sortable">
                  First Name
                  <span class="sort-arrow" :class="sortArrowClass('first_name')">▲</span>
                </th>
                <th @click="sortBy('last_name')" class="th-sortable">
                  Last Name
                  <span class="sort-arrow" :class="sortArrowClass('last_name')">▲</span>
                </th>
                <th @click="sortBy('church')" class="th-sortable tw-hide-sm">
                  Church
                  <span class="sort-arrow" :class="sortArrowClass('church')">▲</span>
                </th>
                <th @click="sortBy('rank')" class="th-sortable tw-hide-sm">
                  Rank
                  <span class="sort-arrow" :class="sortArrowClass('rank')">▲</span>
                </th>
                <th @click="sortBy('percent')" class="th-sortable">
                  Score
                  <span class="sort-arrow" :class="sortArrowClass('percent')">▲</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="filteredSubmissions.length === 0">
                <td colspan="6" class="empty-row">No results match your search.</td>
              </tr>
              <tr
                v-for="(row, i) in sortedSubmissions"
                :key="i"
                class="data-row"
              >
                <td class="td-index">{{ row.index }}</td>
                <td class="td-name">{{ row.first_name }}</td>
                <td class="td-name">{{ row.last_name }}</td>
                <td class="td-meta tw-hide-sm">{{ row.church }}</td>
                <td class="td-meta tw-hide-sm">
                  <span class="rank-badge">{{ row.rank }}</span>
                </td>
                <td class="td-score">
                  <div class="score-wrap">
                    <span class="score-value" :class="scoreClass(row.percent)">{{ row.percent }}%</span>
                    <div class="score-bar-track">
                      <div class="score-bar-fill" :class="scoreClass(row.percent)" :style="{ width: row.percent + '%' }"></div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </main>

    <vue-element-loading v-if="loading" :active="loading" :is-full-screen="true" />
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  data() {
    return {
      submissions: [],
      open: false,
      loading: true,
      sortKey: "index",
      sortDir: 1,
      selectedChurch: '',
      selectedRank: '',
      uniqueChurches: [],
      uniqueRanks: [],
      headers: [
        { text: "First Name",   align: "start", sortable: false, value: "first_name" },
        { text: "Last Name",    value: "last_name" },
        { text: "Church",       value: "church" },
        { text: "Rank",         value: "rank" },
        { text: "Score (100)",  value: "percent" },
      ],
      columns: [
        { label: "S/N",         field: "index" },
        { label: "First Name",  field: "first_name" },
        { label: "Last Name",   field: "last_name" },
        { label: "Church",      field: "church" },
        { label: "Rank",        field: "rank" },
        { label: "Score (100)", field: "percent" },
        // { label: "Score",       field: "score" },
      ],
    };
  },
  computed: {
    filteredSubmissions() {
      let filtered = this.submissions;
      if (this.search) {
        const q = this.search.toLowerCase();
        filtered = filtered.filter(r =>
          [r.first_name, r.last_name, r.church, r.rank]
            .filter(Boolean)
            .some(v => v.toLowerCase().includes(q))
        );
      }
      if (this.selectedChurch) {
        filtered = filtered.filter(r => r.church === this.selectedChurch);
      }
      if (this.selectedRank) {
        filtered = filtered.filter(r => r.rank === this.selectedRank);
      }
      return filtered;
    },
    sortedSubmissions() {
      return [...this.filteredSubmissions].sort((a, b) => {
        const av = a[this.sortKey] ?? '';
        const bv = b[this.sortKey] ?? '';
        if (av < bv) return -1 * this.sortDir;
        if (av > bv) return  1 * this.sortDir;
        return 0;
      });
    },
lowScore() {
  if (!this.filteredSubmissions.length) return 0;
  const min = Math.min(...this.filteredSubmissions.map(r => r.percent));
  return isFinite(min) ? min : 0;
},
highScore() {
  if (!this.filteredSubmissions.length) return 0;
  const max = Math.max(...this.filteredSubmissions.map(r => r.percent));
  return isFinite(max) ? max : 0;
},
passRate() {
  if (!this.filteredSubmissions.length) return 0;
  const passed = this.filteredSubmissions.filter(r => r.percent >= 40).length;
  const rate = (passed / this.filteredSubmissions.length) * 100;
  return isFinite(rate) ? Math.round(rate) : 0;
},
  },
  async mounted() {
    let res = await this.fetchSubmissions();
    let submissions = [];
    let i = 1;
    res.forEach((submission) => {
      submissions.push({
        index:      i++,
        first_name: submission?.user?.first_name,
        last_name:  submission?.user?.last_name,
        church:     submission?.user?.church,
        rank:       submission?.user?.rank,
        percent:    submission.percent,
        score:      submission.score,
      });
    });
    this.loading = false;
    this.submissions = submissions;
    this.uniqueChurches = [...new Set(this.submissions.map(s => s.church).filter(Boolean))].sort();
    this.uniqueRanks = [...new Set(this.submissions.map(s => s.rank).filter(Boolean))].sort();
  },
  methods: {
    ...mapActions(["fetchSubmissions", "submit"]),
    toggle() { this.open = !this.open; },
    sortBy(key) {
      if (this.sortKey === key) { this.sortDir *= -1; }
      else { this.sortKey = key; this.sortDir = 1; }
    },
    sortArrowClass(key) {
      if (this.sortKey !== key) return 'sort-arrow--inactive';
      return this.sortDir === 1 ? 'sort-arrow--asc' : 'sort-arrow--desc';
    },
    scoreClass(pct) {
      if (pct >= 70) return 'score--high';
      if (pct >= 40) return 'score--mid';
      return 'score--low';
    },
    clearFilters() {
      this.selectedChurch = '';
      this.selectedRank = '';
      this.search = '';
    },
    titleCase(str) {
      return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Jost:wght@300;400;500;600&display=swap');

* { box-sizing: border-box; }

/* ── Page ── */
.page-wrapper {
  min-height: 100vh;
  background: #f4f5f9;
  font-family: 'Jost', sans-serif;
  color: #1a1a2e;
  position: relative;
}

.bg-orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  pointer-events: none;
  z-index: 0;
  opacity: 0.07;
}
.bg-orb-1 { width: 600px; height: 600px; background: #c8a96e; top: -200px; right: -150px; }
.bg-orb-2 { width: 500px; height: 500px; background: #3a5f8a; bottom: -150px; left: -100px; }

/* ── Navbar ── */
.navbar {
  position: relative;
  z-index: 10;
  background: #0f1923;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  height: 64px;
  box-shadow: 0 2px 20px rgba(0,0,0,0.25);
}
.nav-brand {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  text-decoration: none;
}
.nav-logo {
  width: 36px; height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 1.5px solid rgba(200,169,110,0.4);
}
.nav-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: #c8a96e;
  letter-spacing: 0.06em;
}
.nav-hamburger {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  color: #fff;
  padding: 0.4rem;
}
.nav-hamburger svg { width: 20px; height: 20px; }
.nav-links { display: flex; align-items: center; gap: 1.5rem; }
.nav-link {
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.65);
  text-decoration: none;
  transition: color 0.2s;
}
.nav-link:hover { color: #c8a96e; }

/* ── Content ── */
.content {
  position: relative;
  z-index: 1;
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem 1.25rem 3rem;
}

/* ── Page Header ── */
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}
.page-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.9rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 0.2rem;
}
.page-subtitle {
  font-size: 0.82rem;
  color: #8a8fa8;
  font-weight: 400;
  margin: 0;
}
.export-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  background: linear-gradient(135deg, #2d7a4f, #3a9960);
  color: #fff;
  font-family: 'Jost', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 4px 14px rgba(45,122,79,0.25);
  text-decoration: none;
}
.export-btn svg { width: 15px; height: 15px; }
.export-btn:hover {
  background: linear-gradient(135deg, #256640, #2f7d4e);
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(45,122,79,0.3);
}

/* ── Stats Row ── */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  border-top: 3px solid #c8a96e;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.stat-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #8a8fa8;
}
.stat-value {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1;
}

/* ── Table Card ── */
.table-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.07);
  overflow: hidden;
}

/* ── Toolbar ── */
.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.1rem 1.5rem;
  border-bottom: 1px solid #f0f2f8;
  flex-wrap: wrap;
}
.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 360px;
}
.search-icon {
  position: absolute;
  left: 0.85rem;
  width: 15px; height: 15px;
  color: #b0b5cc;
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 0.6rem 2.4rem 0.6rem 2.5rem;
  font-family: 'Jost', sans-serif;
  font-size: 0.88rem;
  color: #1a1a2e;
  background: #f7f8fc;
  border: 1.5px solid #e2e5f0;
  border-radius: 9px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.search-input:focus {
  border-color: #c8a96e;
  box-shadow: 0 0 0 3px rgba(200,169,110,0.1);
  background: #fff;
}
.search-input::placeholder { color: #b8bdce; }
.search-clear {
  position: absolute;
  right: 0.7rem;
  background: none;
  border: none;
  cursor: pointer;
  color: #b0b5cc;
  display: flex;
  padding: 0.15rem;
}
.search-clear svg { width: 13px; height: 13px; }
.search-clear:hover { color: #6b7090; }
.results-count {
  font-size: 0.78rem;
  color: #8a8fa8;
  font-weight: 500;
  white-space: nowrap;
}

/* ── Filters ── */
.filter-wrapper {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.filter-select {
  padding: 0.6rem 2rem 0.6rem 0.8rem;
  font-family: 'Jost', sans-serif;
  font-size: 0.88rem;
  color: #1a1a2e;
  background: #f7f8fc url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23b0b5cc" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6,9 12,15 18,9"/></svg>') no-repeat right 0.5rem center;
  background-size: 12px;
  border: 1.5px solid #e2e5f0;
  border-radius: 9px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  appearance: none;
}
.filter-select:focus {
  border-color: #c8a96e;
  box-shadow: 0 0 0 3px rgba(200,169,110,0.1);
  background-color: #fff;
}
.clear-filters-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.6rem 0.8rem;
  background: #f7f8fc;
  color: #6b7090;
  font-family: 'Jost', sans-serif;
  font-size: 0.82rem;
  font-weight: 500;
  border: 1.5px solid #e2e5f0;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
}
.clear-filters-btn svg { width: 12px; height: 12px; }
.clear-filters-btn:hover {
  background: #e8eaf0;
  border-color: #c8a96e;
  color: #1a1a2e;
}

/* ── Skeleton ── */
.skeleton-rows { padding: 0.5rem 0; }
.skeleton-row {
  display: flex;
  gap: 1rem;
  padding: 0.9rem 1.5rem;
  border-bottom: 1px solid #f0f2f8;
}
.skeleton-cell {
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f2f8 25%, #e8eaf0 50%, #f0f2f8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
.skeleton-cell--wide   { width: 22%; }
.skeleton-cell--med    { width: 28%; }
.skeleton-cell--narrow { width: 12%; }
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Table ── */
.table-wrap { overflow-x: auto; }
.results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}
.results-table thead tr {
  background: #f7f8fc;
  border-bottom: 2px solid #e8eaf2;
}
.results-table th {
  padding: 0.85rem 1.25rem;
  text-align: left;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #6b7090;
  white-space: nowrap;
}
.th-sortable {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
}
.th-sortable:hover { color: #1a1a2e; }
.sort-arrow {
  display: inline-block;
  font-size: 0.55rem;
  margin-left: 0.3rem;
  transition: transform 0.2s, opacity 0.2s;
}
.sort-arrow--inactive { opacity: 0.2; }
.sort-arrow--asc      { opacity: 1; transform: rotate(0deg); }
.sort-arrow--desc     { opacity: 1; transform: rotate(180deg); }

.data-row {
  border-bottom: 1px solid #f0f2f8;
  transition: background 0.15s;
}
.data-row:last-child { border-bottom: none; }
.data-row:hover { background: #fafbfd; }

.results-table td {
  padding: 0.85rem 1.25rem;
  color: #3a3f5c;
  vertical-align: middle;
}
.td-index { color: #b0b5cc; font-size: 0.78rem; font-weight: 600; }
.td-name  { font-weight: 500; color: #1a1a2e; text-transform: capitalize; }
.td-meta  { color: #6b7090; font-size: 0.85rem; text-transform: capitalize; }

.rank-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  background: #f0f2f8;
  color: #4a4f6a;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
}

/* Score */
.score-wrap { display: flex; flex-direction: column; gap: 0.3rem; min-width: 80px; }
.score-value {
  font-weight: 700;
  font-size: 0.9rem;
}
.score-bar-track {
  height: 4px;
  background: #f0f2f8;
  border-radius: 2px;
  overflow: hidden;
}
.score-bar-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.6s ease;
}
.score--high { color: #2d7a4f; }
.score--mid  { color: #f4c430; }
.score--low  { color: #c84040; }
.score--high.score-bar-fill { background: #2d7a4f; }
.score--mid.score-bar-fill  { background: #f4c430; }
.score--low.score-bar-fill  { background: #c84040; }

.empty-row {
  text-align: center;
  padding: 3rem 1rem !important;
  color: #b0b5cc;
  font-size: 0.9rem;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .stats-row { grid-template-columns: repeat(2, 1fr); }
  .tw-hide-sm { display: none; }
  .nav-hamburger { display: flex; }
  .nav-links {
    display: none;
    position: absolute;
    top: 64px; left: 0; right: 0;
    background: #0f1923;
    flex-direction: column;
    padding: 1rem 1.5rem;
    gap: 1rem;
    border-top: 1px solid rgba(255,255,255,0.08);
  }
  .nav-links--open { display: flex; }
}
@media (max-width: 480px) {
  .stats-row { grid-template-columns: 1fr 1fr; }
  .page-header { flex-direction: column; align-items: flex-start; }
  .export-btn { width: 100%; justify-content: center; }
  .table-toolbar { flex-direction: column; align-items: stretch; gap: 0.75rem; }
  .search-wrapper { max-width: 100%; }
  .filter-wrapper { flex-wrap: wrap; gap: 0.5rem; }
  .clear-filters-btn { flex: 1; justify-content: center; }
}
</style>